import React from 'react';
import { type ColumnDef } from '@oysterjs/ui/data-table';

import type { HtmlDocumentTemplateFieldsFragment } from '~/api/graphql';

import {
  CreatedByCell,
  EditLinkCell,
  TitleCell,
  UpdatedAtCell,
  selectionColumn
} from '~/components/list/library-table-cells';

export type StyleSortKey = 'title' | 'user' | 'date';
type StyleGroupKey = never;

/** HTML style/template rows share title / createdBy.name / updatedAt for sorting. */
type SortableTemplateRow = {
  title: string;
  createdBy: { name: string };
  updatedAt: Date | string | null | undefined;
};

export function templateListSortFn<T extends SortableTemplateRow>(
  data: readonly T[],
  sortKey: StyleSortKey,
  direction: 'asc' | 'desc'
): T[] {
  const sorted = [...data].sort((a, b) => {
    if (sortKey === 'title') {
      return (a.title || '').localeCompare(b.title || '');
    }
    if (sortKey === 'user') {
      return (a.createdBy.name || '').localeCompare(b.createdBy.name || '');
    }
    const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return aDate - bDate;
  });
  return direction === 'desc' ? sorted.reverse() : sorted;
}

export const styleSortFn = templateListSortFn<HtmlDocumentTemplateFieldsFragment>;

/**
 * Shared title + creator + updated-at + edit-link tail used by both the HTML
 * styles/templates lists and the Word templates list. The select column is
 * supplied by the caller because the row type differs.
 */
function buildTemplateTail<T extends HtmlDocumentTemplateFieldsFragment>(
  editBase: string,
  canEdit?: boolean
): ColumnDef<T, StyleSortKey, StyleGroupKey>[] {
  return [
    {
      id: 'title',
      header: 'Title',
      sortKey: 'title',
      renderCell: (row) => <TitleCell title={row.title} visibility={row.visibility} />
    },
    {
      id: 'user',
      header: 'Created By',
      sortKey: 'user',
      renderCell: (row) => <CreatedByCell name={row.createdBy.name} />
    },
    {
      id: 'date',
      header: 'Last Updated',
      sortKey: 'date',
      headerClassName: 'hidden sm:table-cell',
      cellClassName: 'hidden sm:table-cell',
      renderCell: (row) => <UpdatedAtCell value={row.updatedAt} />
    },
    {
      id: 'actions',
      header: 'Actions',
      noHref: true,
      cellClassName: 'w-0 py-1.5 px-3',
      renderCell: (row) => <EditLinkCell canEdit={canEdit} href={`${editBase}/${row.id}`} />
    }
  ];
}

export function getStyleColumns(opts: {
  selectedSet: ReadonlySet<string>;
  setSelectedTemplates: React.Dispatch<React.SetStateAction<string[]>>;
  filtered: HtmlDocumentTemplateFieldsFragment[];
  /** Edit URL base, e.g. `/app/library/styles` or `/app/library/templates`. */
  editBase: string;
  /** Label used in the "Select all {label}" aria-label. */
  pluralLabel: string;
  /**
   * Whether the caller may change these rows. False drops the bulk-selection
   * column and turns the row link from "Edit" into "View".
   */
  canEdit?: boolean;
}): ColumnDef<HtmlDocumentTemplateFieldsFragment, StyleSortKey, StyleGroupKey>[] {
  return [
    ...(opts.canEdit === false
      ? []
      : [
          selectionColumn<HtmlDocumentTemplateFieldsFragment, StyleSortKey>({
            selectedSet: opts.selectedSet,
            setSelected: opts.setSelectedTemplates,
            filtered: opts.filtered,
            pluralLabel: opts.pluralLabel
          })
        ]),
    ...buildTemplateTail<HtmlDocumentTemplateFieldsFragment>(opts.editBase, opts.canEdit)
  ];
}
